import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Building2, Check, ChevronDown, Plus } from 'lucide-react';
import { useOrganization } from '@/lib/contexts/OrganizationContext';
import { useAuth } from '@/lib/auth/useAuth';
import { listOrganizations, type OrganizationDetails } from '@/api/organizations';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { setCurrentOrganizationId } from '@/lib/api/headers';

interface OrganizationSwitcherProps {
  className?: string;
}

export function OrganizationSwitcher({ className }: OrganizationSwitcherProps) {
  const { organization: currentOrg, loading: orgLoading } = useOrganization();
  const { user: authUser } = useAuth();
  const [organizations, setOrganizations] = useState<OrganizationDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch user's organizations
  useEffect(() => {
    const fetchUserOrganizations = async () => {
      if (!authUser?.organizations || authUser.organizations.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Convert the organizations from auth user to the OrganizationDetails format
        const userOrgs: OrganizationDetails[] = authUser.organizations.map(org => ({
          id: org.id,
          name: org.name,
          slug: org.slug,
          // Add default values for other fields that might be required by OrganizationDetails
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          metadata: {}
        }));
        
        setOrganizations(userOrgs);
      } catch (error) {
        console.error('[OrganizationSwitcher] Error processing organizations:', error);
        toast({
          title: 'Error',
          description: 'Failed to load organizations',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserOrganizations();
  }, [authUser, toast]);

  const handleSwitchOrganization = (orgId: string) => {
    if (orgId === currentOrg?.id) {
      setIsOpen(false);
      return;
    }

    // Set the selected organization ID and prepare headers
    setCurrentOrganizationId(orgId);
    
    // Reload the page to switch context
    // In a more sophisticated implementation, we'd update the OrganizationContext
    // to support switching without a reload
    window.location.reload();
  };

  const handleCreateOrganization = () => {
    setIsOpen(false);
    navigate('/settings?tab=workspace');
  };

  // Don't show switcher if user has only one organization and has a current org
  if (!isLoading && organizations.length <= 1 && currentOrg) {
    return null;
  }

  // Loading state
  if (orgLoading || isLoading) {
    return (
      <div className={cn("flex items-center space-x-2 text-sm", className)}>
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Loading...</span>
      </div>
    );
  }

  // No organization - show "No Organization" state
  if (!currentOrg) {
    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "flex items-center space-x-2 text-sm font-medium text-muted-foreground",
              className
            )}
          >
            <Building2 className="h-4 w-4" />
            <span>No Organization</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[250px]">
          <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {organizations.length > 0 ? (
            <>
              {organizations.map((org) => (
                <DropdownMenuItem
                  key={org.id}
                  onClick={() => handleSwitchOrganization(org.id)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center space-x-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-col">
                      <span className="text-sm">{org.name}</span>
                      {org.slug && (
                        <span className="text-xs text-muted-foreground">
                          {org.slug}
                        </span>
                      )}
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          ) : (
            <DropdownMenuItem disabled>
              <span className="text-sm text-muted-foreground">No organizations available</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={handleCreateOrganization}
            className="cursor-pointer"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span>Create Organization</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "flex items-center space-x-2 text-sm font-medium",
            className
          )}
        >
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span>{currentOrg.name}</span>
          {organizations.length > 1 && (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </DropdownMenuTrigger>
      {organizations.length > 1 && (
        <DropdownMenuContent align="start" className="w-[250px]">
          <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {organizations.map((org) => (
            <DropdownMenuItem
              key={org.id}
              onClick={() => handleSwitchOrganization(org.id)}
              className="cursor-pointer"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span className="text-sm">{org.name}</span>
                    {org.slug && (
                      <span className="text-xs text-muted-foreground">
                        {org.slug}
                      </span>
                    )}
                  </div>
                </div>
                {org.id === currentOrg.id && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </div>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleCreateOrganization}
            className="cursor-pointer"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span>Create Organization</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
}